import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import {
  FOCUS_STOP_WORDS,
  MAX_FOCUS_PREVIEW_LENGTH,
} from "./conversationsSummaryConstants"
import { getMetricLabel } from "./conversationsSummaryMetrics"
import type {
  ConversationsPageSummaryLookups,
  ConversationsSummaryFocus,
} from "./conversationsSummaryTypes"

function normalizeSearchText(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ")
}

function addFocusTerm(terms: Set<string>, term: string) {
  const normalized = normalizeSearchText(term)
  if (
    normalized.length < 3 ||
    FOCUS_STOP_WORDS.has(normalized) ||
    /^\d+$/.test(normalized)
  ) {
    return
  }

  terms.add(normalized)
}

export function parseConversationsSummaryFocusText(
  focusText: string | null | undefined,
): ConversationsSummaryFocus {
  const rawText = (focusText ?? "").trim()
  const terms = new Set<string>()

  rawText
    .split(/[\n,;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => addFocusTerm(terms, part))

  rawText
    .match(/[a-zA-Z0-9][a-zA-Z0-9_-]{2,}/g)
    ?.forEach((token) => addFocusTerm(terms, token))

  return {
    hasFocus: rawText.length > 0,
    rawText,
    terms: Array.from(terms),
  }
}

function focusTextMatches(
  value: string | null | undefined,
  focus: ConversationsSummaryFocus,
): boolean {
  if (!focus.hasFocus || focus.terms.length === 0 || !value) {
    return false
  }

  const normalizedValue = normalizeSearchText(value)
  return focus.terms.some((term) => normalizedValue.includes(term))
}

export function getAgentLabel(
  agentId: string | null | undefined,
  lookups: ConversationsPageSummaryLookups | undefined,
): string {
  if (!agentId) {
    return "Unknown agent"
  }

  return lookups?.agentNames?.[agentId] ?? agentId.slice(0, 8)
}

export function getMetricIds(row: ConversationsTableResult): string[] {
  return Array.from(
    new Set([
      ...Object.keys(row.metrics ?? {}),
      ...Object.keys(row.metric_error_statuses ?? {}),
    ]),
  )
}

export function metricMatchesFocus(
  metricId: string,
  lookups: ConversationsPageSummaryLookups | undefined,
  focus: ConversationsSummaryFocus,
): boolean {
  return (
    focusTextMatches(metricId, focus) ||
    focusTextMatches(getMetricLabel(metricId, lookups), focus)
  )
}

export function metadataMatchesFocus(
  key: string,
  value: string,
  focus: ConversationsSummaryFocus,
): boolean {
  return (
    focusTextMatches(key, focus) ||
    focusTextMatches(value, focus) ||
    focusTextMatches(`${key}: ${value}`, focus)
  )
}

export function formatMetadataValue(value: unknown): string | null {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    const formattedValue = String(value).trim()
    return formattedValue.length > 0 ? formattedValue : null
  }

  return null
}

export function rowMatchesFocus(
  row: ConversationsTableResult,
  lookups: ConversationsPageSummaryLookups | undefined,
  focus: ConversationsSummaryFocus,
): boolean {
  if (!focus.hasFocus || focus.terms.length === 0) {
    return false
  }

  if (
    focusTextMatches(row.agent_id, focus) ||
    focusTextMatches(getAgentLabel(row.agent_id, lookups), focus) ||
    focusTextMatches(row.run_status, focus) ||
    focusTextMatches(row.error_status, focus)
  ) {
    return true
  }

  if ((row.tags ?? []).some((tag) => focusTextMatches(tag, focus))) {
    return true
  }

  if (
    getMetricIds(row).some((metricId) =>
      metricMatchesFocus(metricId, lookups, focus),
    )
  ) {
    return true
  }

  return Object.entries(row.customer_metadata ?? {}).some(([key, value]) => {
    const formattedValue = formatMetadataValue(value)
    return formattedValue
      ? metadataMatchesFocus(key, formattedValue, focus)
      : false
  })
}

export function formatFocusPreview(focus: ConversationsSummaryFocus): string {
  return focus.rawText.length > MAX_FOCUS_PREVIEW_LENGTH
    ? `${focus.rawText.slice(0, MAX_FOCUS_PREVIEW_LENGTH).trimEnd()}...`
    : focus.rawText
}
